<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    class="login-form"
    @keyup.enter="handleSubmit"
  >
    <el-form-item prop="phone">
      <el-input
        v-model="form.phone"
        placeholder="请输入手机号"
        size="large"
        :prefix-icon="Phone"
        maxlength="11"
      />
    </el-form-item>

    <el-form-item prop="captchaCode">
      <div class="captcha-row">
        <el-input
          v-model="form.captchaCode"
          placeholder="请输入图形验证码"
          size="large"
          :prefix-icon="CircleCheck"
          maxlength="6"
          class="captcha-input"
        />
        <div
          class="captcha-image"
          :class="{ loading: captchaLoading }"
          @click="refreshCaptcha"
        >
          <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
          <el-icon v-else><Loading /></el-icon>
        </div>
      </div>
    </el-form-item>

    <el-form-item prop="smsCode">
      <div class="sms-code-row">
        <el-input
          v-model="form.smsCode"
          placeholder="请输入短信验证码"
          size="large"
          :prefix-icon="Message"
          maxlength="6"
          class="sms-input"
        />
        <el-button
          size="large"
          :disabled="countdown > 0 || smsLoading"
          :loading="smsLoading"
          class="send-btn"
          @click="sendSmsCode"
        >
          {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
        </el-button>
      </div>
    </el-form-item>

    <el-button
      type="primary"
      size="large"
      class="submit-btn"
      :loading="loading"
      @click="handleSubmit"
    >
      登录
    </el-button>
  </el-form>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { Phone, Message, CircleCheck, Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import { sendLoginSmsCode, loginBySms } from '@/api/auth'
import { useUserStore } from '@/stores/user'
import { useCaptcha } from '@/composables/useCaptcha'
import type { ApiResponse } from '@/types'
import type { LoginResponse } from '@/types/user'

const props = defineProps<{
  phone?: string
}>()

const emit = defineEmits<{
  success: []
}>()

const { captchaLoading, captchaImage, captchaKey, refreshCaptcha } = useCaptcha()

onMounted(() => {
  refreshCaptcha()
})

const userStore = useUserStore()
const formRef = ref<FormInstance>()
const loading = ref(false)
const smsLoading = ref(false)
const countdown = ref(0)
let countdownTimer: number | null = null

const form = ref({
  phone: props.phone || '',
  captchaCode: '',
  smsCode: '',
})

if (props.phone) {
  form.value.phone = props.phone
}

const rules: FormRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' },
  ],
  smsCode: [
    { required: true, message: '请输入短信验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' },
  ],
}

const startCountdown = () => {
  countdown.value = 60
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0 && countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
}

const sendSmsCode = async () => {
  const phoneValid = await formRef.value?.validateField('phone').catch(() => false)
  const captchaValid = await formRef.value?.validateField('captchaCode').catch(() => false)
  if (!phoneValid || !captchaValid) return

  smsLoading.value = true
  try {
    await sendLoginSmsCode(form.value.phone, captchaKey.value, form.value.captchaCode)
    ElMessage.success('验证码已发送')
    startCountdown()
  } catch {
    refreshCaptcha()
    form.value.captchaCode = ''
  } finally {
    smsLoading.value = false
  }
}

const handleSubmit = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid) => {
    if (!valid) return

    loading.value = true
    try {
      const res: ApiResponse<LoginResponse> = await loginBySms({
        phone: form.value.phone,
        smsCode: form.value.smsCode,
      })

      const userState = {
        userId: res.data.userId,
        realName: res.data.realName,
        phone: res.data.phone,
        avatar: res.data.avatar,
        isVip: res.data.isVip,
        vipExpireTime: res.data.vipExpireTime,
      }

      userStore.setUserInfo(userState)
      userStore.setToken(res.data.accessToken)
      userStore.setRefreshToken(res.data.refreshToken)

      emit('success')
    } catch (error) {
      // 错误已在拦截器处理
    } finally {
      loading.value = false
    }
  })
}

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped lang="scss">
.login-form {
  :deep(.el-input__inner) {
    height: 44px;
  }
}

.captcha-row {
  display: flex;
  gap: 12px;

  .captcha-input {
    flex: 1;
  }

  .captcha-image {
    width: 120px;
    height: 44px;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.loading {
      opacity: 0.6;
    }
  }
}

.sms-code-row {
  display: flex;
  gap: 12px;

  .sms-input {
    flex: 1;
  }

  .send-btn {
    width: 120px;
    height: 44px;
    padding: 0;
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
}
</style>
